// Navigation der Verwaltung (Spezifikation 3.1–3.3): eine einzige Quelle für Seitenleiste, Rail, Touch-Rail, Flyouts,
// Mehr-Menü, Tab-Bar, Aktiv-Erkennung, Seitentitel (AppPage, App-Leiste, document.title).
// Die Sichtbarkeitsregeln sind 1:1 aus der bisherigen MainLayout.vue übernommen (Rechtenamen exakt, auch
// VIEW_ANALYTICS__TRACK_MENU mit zwei Unterstrichen). Rechte steuern nur die Sichtbarkeit; der Router prüft weiterhin
// nur den Login.
// Navigation immer per <router-link> bzw. router.push(path).catch(() => {}), nie per Routenname (doppelte Namen).
import { hasPermission, isAdmin } from '@/auth/auth';

const always = () => true;
const never = () => false;
const perm = name => () => hasPermission(name);

function freezeAll(list) {
  return Object.freeze(list.map((item) => {
    const copy = Object.assign({}, item);
    if (item.children) {
      copy.children = freezeAll(item.children);
    }
    return Object.freeze(copy);
  }));
}

// Datenform: { id, title, icon, path?, visible: () => Boolean, children? }, Kinder { title, icon, path, visible }.
// Reihenfolge = Reihenfolge in Seitenleiste, Rail und Mehr-Menü.
export const NAVIGATION = freezeAll([
  { id: 'uebersicht', title: 'Dashboard', icon: 'grid', path: '/overview', visible: always },
  {
    id: 'verwaltung', title: 'Verwaltung', icon: 'briefcase', visible: perm('VIEW_ADMINISTRATION_MENU'),
    children: [
      { title: 'Verkäufe', icon: 'receipt', path: '/administration/ticketsales', visible: perm('VIEW_ADMINISTRATION_TICKETSALE_MENU') },
      // erscheint erst, wenn die Route existiert (Gruppe 3)
      { title: 'Exporte', icon: 'fileDown', path: '/administration/exports', visible: perm('VIEW_ADMINISTRATION_TICKETSALE_MENU') },
      { title: 'Zahlungen', icon: 'card', path: '/administration/payments', visible: perm('VIEW_ADMINISTRATION_PAYMENT_MENU') },
      { title: 'Saldo', icon: 'scale', path: '/administration/saldo', visible: perm('VIEW_ADMINISTRATION_SALDO_MENU') },
      // Reihenfolge seit 05.11.2026 (Kundenwunsch: „fahrerabrechnung zuerst dann Rollen dann Druckerlog“)
      { title: 'Fahrerabrechnung', icon: 'printer', path: '/administration/driversettlement', visible: perm('VIEW_ADMINISTRATION_TICKETSALE_MENU') },
      // Rollenverfolgung der Thermorollen (Kundenwunsch 27.10.2026: bei allen Kunden, Inhalt nur aus den letzten 30 Tagen)
      { title: 'Rollen', icon: 'scroll', path: '/administration/rolls', visible: perm('VIEW_ADMINISTRATION_TICKETSALE_MENU') },
      // alle Drucke (Verkäufe vereinfacht, Drucke ohne Verkauf, Druckerzustände) mit Zeit, Ort, Benutzer; Recht wie
      // Rollen. Bleibt der letzte Eintrag von Verwaltung
      { title: 'Druckprotokoll', icon: 'clock', path: '/administration/printlog', visible: perm('VIEW_ADMINISTRATION_TICKETSALE_MENU') }
    ]
  },
  {
    // bisher „Analytics“
    id: 'auswertungen', title: 'Auswertungen', icon: 'activity', visible: perm('VIEW_ANALYTICS_MENU'),
    children: [
      { title: 'Live-Tracking', icon: 'pin', path: '/analytics/live', visible: perm('VIEW_ANALYTICS_LIVE_MENU') },
      { title: 'Fahrplanübersicht', icon: 'route', path: '/analytics/tracks', visible: perm('VIEW_ANALYTICS__TRACK_MENU') }
    ]
  },
  {
    id: 'stammdaten', title: 'Stammdaten', icon: 'database', visible: perm('VIEW_MASTERDATA_MENU'),
    children: [
      { title: 'Benutzer', icon: 'users', path: '/masterdata/users', visible: perm('VIEW_MASTERDATA_USER_MENU') },
      { title: 'Geräte', icon: 'tablet', path: '/masterdata/devices', visible: perm('VIEW_MASTERDATA_DEVICE_MENU') },
      { title: 'Fahrpläne', icon: 'calClock', path: '/masterdata/versions', visible: perm('VIEW_MASTERDATA_TIMETABLE_MENU') },
      { title: 'Dokumente', icon: 'file', path: '/masterdata/documents', visible: perm('VIEW_MASTERDATA_TIMETABLE_MENU') },
      { title: 'Logos', icon: 'image', path: '/masterdata/logo', visible: perm('VIEW_MASTERDATA_TIMETABLE_MENU') },
      { title: 'Orte', icon: 'map', path: '/masterdata/locations', visible: perm('VIEW_MASTERDATA_LOCATION_MENU') },
      { title: 'Preise', icon: 'euro', path: '/masterdata/price', visible: always },
      { title: 'Ticketarten', icon: 'ticket', path: '/masterdata/tickettypes', visible: always }
    ]
  },
  {
    // im Menü ausgeblendet wie bisher (Frage F1); die Seiten bleiben per URL erreichbar und haben Titel
    id: 'tarif', title: 'Tarifverwaltung', icon: 'tag', visible: never,
    children: [
      { title: 'Tarifzonen', icon: 'map', path: '/tariff/tariffzones', visible: always },
      { title: 'Ticketarten', icon: 'ticket', path: '/tariff/tickettypes', visible: always },
      { title: 'Tarifversionen', icon: 'calClock', path: '/tariff/tariffversions', visible: always },
      { title: 'Tarifstufen', icon: 'tag', path: '/tariff/tarifflevels', visible: always },
      { title: 'Preisstufen', icon: 'euro', path: '/tariff/pricelevels', visible: always }
    ]
  },
  {
    id: 'shop', title: 'Shop', icon: 'bag', visible: isAdmin,
    children: [
      { title: 'Fahrten', icon: 'bus', path: '/shop/trips', visible: always },
      { title: 'Tickets', icon: 'ticket', path: '/shop/tickets', visible: always },
      { title: 'Stripe', icon: 'card', path: '/shop/payments', visible: always },
      { title: 'PayPal', icon: 'wallet', path: '/shop/paypal', visible: always },
      { title: 'Auszahlungen', icon: 'banknote', path: '/shop/payouts', visible: always },
      { title: 'Fahrzeugtypen', icon: 'bus', path: '/shop/vehicles', visible: always },
      { title: 'MwSt-Sätze', icon: 'percent', path: '/shop/vatrates', visible: always },
      { title: 'MwSt-Beziehungen', icon: 'percent', path: '/shop/vatrelations', visible: always }
    ]
  },
  {
    // Pfade mit Tippfehler „bavariatimtable“ bleiben (so heißen die Routen)
    id: 'schnittstellen', title: 'Schnittstellen', icon: 'plug', visible: isAdmin,
    children: [
      // Bezeichnungen seit 13.10.2026 (Kundenwunsch, zuletzt: „Echtzeit DEFAS und Google GTFS bitte“)
      { title: 'Echtzeit DEFAS', icon: 'radio', path: '/bavariatimtable/actualdata', visible: always },
      { title: 'Google GTFS', icon: 'map', path: '/bavariatimtable/gtfs', visible: always }
    ]
  },
  {
    id: 'einstellungen', title: 'Einstellungen', icon: 'sliders', visible: always,
    children: [
      { title: 'Mein Profil', icon: 'user', path: '/settings/profile', visible: always },
      { title: 'Kundeneinstellungen', icon: 'sliders', path: '/settings/customer', visible: isAdmin }
    ]
  },
  {
    // Icon shield nicht entworfen
    id: 'superadmin', title: 'Superadmin', icon: 'shield', visible: perm('VIEW_SUPERADMIN_MENU'),
    children: [
      { title: 'Online', icon: 'wifi', path: '/superadmin/online', visible: perm('VIEW_SUPERADMIN_ONLINE_MENU') },
      { title: 'Geräte', icon: 'tablet', path: '/superadmin/device', visible: always },
      { title: 'Stripe', icon: 'card', path: '/superadmin/stripe', visible: always },
      { title: 'Neuer Kunde', icon: 'userPlus', path: '/superadmin/newcustomer', visible: always }
    ]
  }
]);

// Seiten ohne Menüpunkt: Titel und Icon nur für Seitenkopf, App-Leiste und document.title
export const EXTRA_TITLES = Object.freeze({
  '/shop/tickettemplates': Object.freeze({ title: 'Ticketvorlage', icon: 'file', path: '/shop/tickettemplates' }),
  '/eticket/orderer': Object.freeze({ title: 'Besteller', icon: 'users', path: '/eticket/orderer' }),
  '/eticket/orderlist': Object.freeze({ title: 'Bestellliste', icon: 'file', path: '/eticket/orderlist' }),
  '/eticket/tickets': Object.freeze({ title: 'E-Tickets', icon: 'ticket', path: '/eticket/tickets' })
});

// Kleinschreibung, ohne Query/Hash und ohne abschließenden Schrägstrich: „/overview/“ → „/overview“,
// die Route „/shop/Trips“ passt damit zum Menülink „/shop/trips“.
export function normalizePath(path) {
  let p = String(path || '').split(/[?#]/)[0].toLowerCase();
  if (p.length > 1) {
    p = p.replace(/\/+$/, '');
  }
  return p || '/';
}

// Pfad → { group, entry } aus der vollständigen Definition (auch ausgeblendete Tarif-Seiten)
const INDEX = (() => {
  const index = {};
  NAVIGATION.forEach((group) => {
    if (group.path) {
      index[normalizePath(group.path)] = Object.freeze({ group, entry: group });
    }
    (group.children || []).forEach((entry) => {
      const key = normalizePath(entry.path);
      if (!index[key]) {
        index[key] = Object.freeze({ group, entry });
      }
    });
  });
  Object.keys(EXTRA_TITLES).forEach((path) => {
    const key = normalizePath(path);
    if (!index[key]) {
      index[key] = Object.freeze({ group: null, entry: EXTRA_TITLES[path] });
    }
  });
  return index;
})();

const NOT_FOUND = Object.freeze({ group: null, entry: null });

// { group, entry }: group = Navigationsgruppe (Aktiv-Erkennung), entry = Seite (Titel, Icon). Hauptpunkte ohne Kinder
// (Übersicht) sind zugleich group und entry; Seiten aus EXTRA_TITLES haben keine group; unbekannt: beide null.
export function findEntry(path) {
  return INDEX[normalizePath(path)] || NOT_FOUND;
}

// Seitentitel für App-Leiste und document.title ('' wenn unbekannt)
export function pageTitle(path) {
  const found = findEntry(path);
  if (found.entry) {
    return found.entry.title;
  }
  return found.group ? found.group.title : '';
}

function routeExists(router, path) {
  if (!router || typeof router.match !== 'function') {
    return true;
  }
  try {
    return router.match(path).matched.length > 0;
  } catch (e) {
    return false;
  }
}

// Sichtbare Navigation für das angemeldete Profil: Einträge nach visible() und nur, wenn ihr Pfad zu einer Route passt
// („Exporte“ erscheint, sobald Gruppe 3 die Route anlegt); Gruppen ohne sichtbares Kind entfallen.
// In computed-Eigenschaften aufrufen: reaktiv auf store.state.profile.
export function visibleNavigation(router) {
  const result = [];
  NAVIGATION.forEach((group) => {
    if (!group.visible()) {
      return;
    }
    if (group.children) {
      const children = group.children.filter(entry => entry.visible() && routeExists(router, entry.path));
      if (children.length) {
        result.push(Object.freeze(Object.assign({}, group, { children: Object.freeze(children) })));
      }
    } else if (!group.path || routeExists(router, group.path)) {
      result.push(group);
    }
  });
  return result;
}

// Pfad ist in der sichtbaren Navigation enthalten (Links auf der Übersicht nur dann verlinken)
export function isPathAllowed(path, router) {
  const key = normalizePath(path);
  return visibleNavigation(router).some(group => (
    (group.path && normalizePath(group.path) === key) ||
    (group.children || []).some(entry => normalizePath(entry.path) === key)
  ));
}

// Kundendokumentation (Benutzermenü, Mehr-Menü; bisher auf der Startseite), öffnet in neuem Tab
export const DOCS_URL = 'https://bconn.atlassian.net/wiki/spaces/Kundendokumentation/overview';
// Mobility Hub (Kerbe der Hub-Shell, Hub-Benutzermenü, Mehr-Menü): im selben Fenster, dort eigene Anmeldung
export const MOBILITY_HUB_URL = 'https://mobilityhub.simasoftware.de';

// ---------------------------------------------------------------- Benutzerblock (3.3)
// Feste Rollenbezeichnungen (Frage F4); unbekannte Codes ergeben keine Rollenzeile. Alles null-sicher: zwischen
// dispatch('signOut') und dem Routenwechsel ist profile null.
export const ROLE_LABELS = Object.freeze({ ADMIN: 'Administrator', DRIVER: 'Fahrer', SUPERADMIN: 'Superadmin' });

export function roleLabel(role) {
  return Object.prototype.hasOwnProperty.call(ROLE_LABELS, role) ? ROLE_LABELS[role] : null;
}

export function userDisplayName(profile) {
  return profile ? (profile.fullname || profile.username || '') : '';
}

// Mandantenwechsel für alle angemeldeten Benutzer (Kundenwunsch 12.10.2026; vorher nur mit Superadmin-Recht). Sicher,
// weil der Wechsel die Anmeldung beim Zielmandanten verlangt (AppTenantSwitch) und die Kundenliste dieselbe öffentliche
// ist wie auf der Anmeldeseite. Ohne Profil (beim Abmelden) nicht.
export function tenantSwitchAllowed(state) {
  return !!(state && state.profile);
}

// Hauptfarbe wählbar (Kundenwunsch 13.10.2026: „die wählbare hauptfarbe soll bitte nur für bconn_admin verfügbar sein,
// ansonsten jetzt erstmal petrol“): nur für den Login „bconn_admin“ (Profil oder Cognito-Name „12_bconn_admin“); alle
// anderen sehen Petrol und im Menü „Darstellung“ keine Auswahl
export const HAUPTFARBE_LOGIN = 'bconn_admin';
export function hauptfarbeWaehlbar(state) {
  if (!state) {
    return false;
  }
  const login = state.profile && state.profile.username ? String(state.profile.username).toLowerCase() : '';
  const cognito = state.cognitoUsername ? String(state.cognitoUsername).toLowerCase() : '';
  return login === HAUPTFARBE_LOGIN || cognito.slice(cognito.indexOf('_') + 1) === HAUPTFARBE_LOGIN;
}

// Mandantenlisten (Auswahl in der Systemleiste, Dialog „Mandant wechseln“): nach Kundennummer sortiert
export function byCustomerId(a, b) {
  return Number(a.customer_id) - Number(b.customer_id);
}

// Benutzername (Anmeldename aus dem Profil) für die Zeile unter dem Namen; leer, wenn er schon als Name dient
export function userLoginName(profile) {
  const username = profile && profile.username ? String(profile.username) : '';
  return username && username !== userDisplayName(profile) ? username : '';
}

// Kundenname: Kundendaten (GET customer), sonst Profil, sonst Name aus der Anmeldung
export function customerDisplayName(state) {
  if (!state) {
    return '';
  }
  if (state.customer && state.customer.cust_printname) {
    return state.customer.cust_printname;
  }
  if (state.profile && state.profile.customer && state.profile.customer.cust_printname) {
    return state.profile.customer.cust_printname;
  }
  return state.customerName || '';
}
